import type { ColorScheme } from '@mantine/core';
import {
	ColorSchemeProvider,
	createEmotionCache,
	MantineProvider,
	Tooltip,
} from '@mantine/core';
import { useColorScheme, useHotkeys, useLocalStorage } from '@mantine/hooks';
import { ApiConfigProvider } from '@repo/api-codegen/api/codegen/apiConfigContext';
import { trackEvent } from '@repo/common/analytics';
import type { NormalizerConfig } from '@repo/react-query-cache';
import { QueryNormalizerProvider } from '@repo/react-query-cache';
import { SecodaTheme } from '@repo/theme/mantine/secodaTheme';
import { QueryClientProvider } from '@tanstack/react-query';
import 'focus-visible/dist/focus-visible';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { queryClient, useMaybeAuthUser } from './api';
import { TableV2Spotlight } from './components/Spotlight/components/TableV2Spotlight';
import { GLOBAL_SHORTCUTS } from './constants/shortcuts';
import { FeatureFlagsProvider } from './utils/featureFlags';
import { LaunchDarklyProvider } from './utils/featureFlags/LaunchDarklyProvider';

const QUERY_NORMALIZER_CONFIG: NormalizerConfig = {
	devLogging: false,
};

const myCache = createEmotionCache({
	key: 'mantine',
	prepend: true,
	speedy: true,
});

function ThemedContent({ children }: { children: React.ReactNode }) {
	const { user } = useMaybeAuthUser();

	const systemColorScheme = useColorScheme();
	// eslint-disable-next-line no-local-storage/no-local-storage
	const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
		key: 'mantine-color-scheme',
		defaultValue: systemColorScheme,
		getInitialValueInEffect: false,
	});

	const toggleColorScheme = useCallback(
		(value?: ColorScheme) =>
			setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark')),
		[colorScheme, setColorScheme]
	);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		// Persist the user's preferred color scheme in local storage so we load the
		// correct theme quicker
		if (!isNil(user?.user_appearance)) {
			if (user.user_appearance === 'system') {
				setColorScheme(systemColorScheme);
			} else {
				setColorScheme(user.user_appearance as ColorScheme);
			}
		}
	}, [user, setColorScheme, systemColorScheme]);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useLayoutEffect(() => {
		trackEvent('theme', { colorScheme });
	}, [colorScheme]);

	useHotkeys([
		[GLOBAL_SHORTCUTS.TOGGLE_COLOR_SCHEME[0], () => toggleColorScheme()],
	]);

	return (
		<ColorSchemeProvider
			colorScheme={colorScheme}
			toggleColorScheme={toggleColorScheme}
		>
			<MantineProvider
				theme={{ ...SecodaTheme, colorScheme: colorScheme }}
				withNormalizeCSS
				withGlobalStyles
				emotionCache={myCache}
				// Necessary for markdown renderer
				withCSSVariables
			>
				<Tooltip.Group openDelay={1000} closeDelay={200}>
					<TableV2Spotlight>{children}</TableV2Spotlight>
				</Tooltip.Group>
			</MantineProvider>
		</ColorSchemeProvider>
	);
}

export function ReactPortalReusableProviders({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<DndProvider backend={HTML5Backend}>
			<QueryNormalizerProvider
				queryClient={queryClient}
				normalizerConfig={QUERY_NORMALIZER_CONFIG}
			>
				<QueryClientProvider client={queryClient}>
					<ApiConfigProvider>
						<LaunchDarklyProvider>
							<FeatureFlagsProvider>
								<ThemedContent>{children}</ThemedContent>
							</FeatureFlagsProvider>
						</LaunchDarklyProvider>
					</ApiConfigProvider>
				</QueryClientProvider>
			</QueryNormalizerProvider>
		</DndProvider>
	);
}
